// moment-timezone (а не moment): .tz() требует плагина — в бандле он мог
// подтягиваться транзитивно чужими импортами, в jest — нет. Паттерн проекта —
// CallTimeClientTime.jsx.
import moment from 'moment-timezone';

import type {
  ClientActivity,
  ClientActivityDateTime,
  ClientActivityDevice,
  ClientActivityEvent,
} from './store/activityStore';

// Размер выборки событий для дашбордов «События» и «Top-10 URI»
// (максимум бэка — 500, события идут новые первыми).
export const ACTIVITY_SAMPLE_SIZE = 500;

// Русские названия типов событий (enum ClientActivityEventType на бэке)
export const EVENT_TYPE_TITLES: Record<string, string> = {
  FIRST_LOGIN: 'Первый вход',
  LOGIN: 'Вход',
  REGISTER: 'Регистрация',
  API_REQUEST: 'Запрос API',
  WIDGET_OPEN: 'Открытие виджета',
  ORDER_CREATED: 'Создание заявки',
  PAYMENT_STARTED: 'Начало оплаты',
  PAYMENT_SUCCESS: 'Оплата прошла',
};

// Дата-время с бэка: ISO-строка или массив Jackson WRITE_DATES_AS_TIMESTAMPS
// ([год, месяц, день, ч, мин, сек, нанос]) — зависит от инстанса.
// Показываем по Москве, как в отчётах проекта.
export const formatEventTime = (value?: ClientActivityDateTime): string => {
  if (!value) {
    return '';
  }

  const momentValue = Array.isArray(value)
    ? moment.utc([value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0])
    : moment.utc(String(value).replace(/\.\d+$/, ''));

  return momentValue.tz('Europe/Moscow').format('DD.MM.YYYY HH:mm:ss');
};

// Сортировочное значение даты (массив/строка → timestamp); пусто — 0
const dateTimeValue = (value?: ClientActivityDateTime): number => {
  if (!value) {
    return 0;
  }

  return Array.isArray(value)
    ? Date.UTC(value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0)
    : moment.utc(String(value).replace(/\.\d+$/, '')).valueOf();
};

export interface DimensionPoint {
  label: string,
  count: number,
  /** Доля от суммы весов разреза; null — когда сумма 0. */
  share: number | null,
}

const OTHER_LABEL = 'Прочие';

const toDimension = (counts: Map<string, number>, top: number): DimensionPoint[] => {
  const total = Array.from(counts.values()).reduce((sum, count) => sum + count, 0);
  if (total === 0) {
    return [];
  }

  const sorted = Array.from(counts.entries())
    .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));

  const head = sorted.slice(0, top);
  const rest = sorted.slice(top);
  const points = head.map(([label, count]) => ({
    label,
    count,
    share: count / total,
  }));

  if (rest.length > 0) {
    const restCount = rest.reduce((sum, [, count]) => sum + count, 0);
    points.push({ label: OTHER_LABEL, count: restCount, share: restCount / total });
  }

  return points;
};

// Разрез по устройствам: вес устройства — seenCount (число запросов за всю
// историю), данные точные, без выборок. Пустой ярлык → «Не определён».
export const buildDeviceDimension = (
  devices: ClientActivityDevice[],
  getLabel: (device: ClientActivityDevice) => string | undefined,
  top = 5
): DimensionPoint[] => {
  const counts = new Map<string, number>();

  devices.forEach((device) => {
    const rawLabel = getLabel(device);
    const label = rawLabel?.trim() ? rawLabel.trim() : 'Не определён';
    counts.set(label, (counts.get(label) || 0) + Math.max(device.seenCount, 0));
  });

  return toDimension(counts, top);
};

// Разрез по типам событий — по выборке событий (последние ACTIVITY_SAMPLE_SIZE)
export const buildEventTypeDimension = (events: ClientActivityEvent[]): DimensionPoint[] => {
  const counts = new Map<string, number>();

  events.forEach((event) => {
    const label = EVENT_TYPE_TITLES[event?.eventType] ?? event?.eventType ?? 'Не определён';
    counts.set(label, (counts.get(label) || 0) + 1);
  });

  return toDimension(counts, 5);
};

// URI без query-строки (?…) — разные query одного эндпоинта считаем вместе
const uriPath = (uri?: string): string => (uri ? uri.split('?', 2)[0] : '—');

export interface TopUriPoint extends DimensionPoint {
  /** URI целиком (с query), как пришёл, — для тултипа топ-значения. */
  original: string,
}

// Top-N URI запросов — по выборке событий
export const buildTopUriDimension = (events: ClientActivityEvent[], top = 10): TopUriPoint[] => {
  const counts = new Map<string, number>();
  const originals = new Map<string, string>();

  events.forEach((event) => {
    const path = uriPath(event?.requestUri);
    counts.set(path, (counts.get(path) || 0) + 1);
    if (!originals.has(path)) {
      originals.set(path, event?.requestUri ?? path);
    }
  });

  const total = Array.from(counts.values()).reduce((sum, count) => sum + count, 0);
  if (total === 0) {
    return [];
  }

  return Array.from(counts.entries())
    .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
    .slice(0, top)
    .map(([label, count]) => ({
      label,
      original: originals.get(label) ?? label,
      count,
      share: count / total,
    }));
};

export interface ActivityKpi {
  totalEvents: number,
  devicesCount: number,
  requestsCount: number,
  firstSeen: string,
  lastSeen: string,
}

// KPI-строка вкладки: точные значения из ответа /activity (без выборок)
export const buildActivityKpi = (activity: ClientActivity): ActivityKpi => {
  const devices = activity?.devices ?? [];

  // Устройство с min/max датой (пустые даты пропускаем)
  const deviceByDate = (
    picker: (a: ClientActivityDevice, b: ClientActivityDevice) => ClientActivityDevice,
    getDate: (device: ClientActivityDevice) => ClientActivityDateTime
  ): ClientActivityDevice | null => devices.reduce<ClientActivityDevice | null>((best, device) => {
    if (!dateTimeValue(getDate(device))) {
      return best;
    }
    return best ? picker(best, device) : device;
  }, null);

  const first = deviceByDate(
    (a, b) => (dateTimeValue(a.firstSeenAt) <= dateTimeValue(b.firstSeenAt) ? a : b),
    (device) => device.firstSeenAt
  );
  const last = deviceByDate(
    (a, b) => (dateTimeValue(a.lastSeenAt) >= dateTimeValue(b.lastSeenAt) ? a : b),
    (device) => device.lastSeenAt
  );

  return {
    totalEvents: activity?.total ?? 0,
    devicesCount: devices.length,
    requestsCount: devices.reduce((sum, device) => sum + Math.max(device?.seenCount ?? 0, 0), 0),
    firstSeen: first ? formatEventTime(first.firstSeenAt) : '',
    lastSeen: last ? formatEventTime(last.lastSeenAt) : '',
  };
};
